import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAnnouncements, getUpcomingEvents, getRecentSermons, getGallery } from '../services/api';
import './Home.css';

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
}

export default function Home() {
  const [announcements, setAnnouncements] = useState([]);
  const [events, setEvents] = useState([]);
  const [sermons, setSermons] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    getAnnouncements().then(setAnnouncements).catch(() => {});
    getUpcomingEvents().then(setEvents).catch(() => {});
    getRecentSermons().then(setSermons).catch(() => {});
    getGallery().then((imgs) => setGalleryImages(imgs.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div className="home">
      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="hero">
        <div className="hero__overlay" />
        <div className="hero__content container">
          <span className="hero__eyebrow">Bienvenue à la</span>
          <h1 className="hero__title">Première Église Chrétienne De La Chambre Haute</h1>
          <p className="hero__subtitle">Saint-Marc, Haïti</p>
          <p className="hero__verse">
            "Car c'est par la grâce que vous êtes sauvés, par le moyen de la foi."
            <br /><em>— Éphésiens 2:8</em>
          </p>
          <div className="hero__actions">
            <Link to="/about" className="btn btn-primary">Découvrir notre église</Link>
            <Link to="/contact" className="btn btn-outline">Nous rejoindre</Link>
          </div>
        </div>
        <div className="hero__scroll-hint">↓</div>
      </section>

      {/* ── Service Times ─────────────────────────────────────────────── */}
      <section className="service-times">
        <div className="container">
          <div className="times-grid">
            {[
              { day: 'Dimanche', time: '7h00 AM', label: 'École du Dimanche' },
              { day: 'Dimanche', time: '9h00 AM', label: 'Culte Principal' },
              { day: 'Mercredi', time: '6h00 PM', label: 'Combat Spirituel' },
              { day: 'Vendredi', time: '9h00 PM', label: 'Veillée de nuit' },
            ].map(({ day, time, label }) => (
              <div key={label} className="time-card">
                <span className="time-card__day">{day}</span>
                <span className="time-card__time">{time}</span>
                <span className="time-card__label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Welcome ───────────────────────────────────────────────────── */}
      <section className="section welcome-section">
        <div className="container welcome-grid">
          <div className="welcome-text">
            <h2 className="section-title">Bienvenue dans notre famille</h2>
            <div className="gold-bar" />
            <p>
              La Première Église Chrétienne De La Chambre Haute est une communauté chrétienne vivante, fondée sur l'amour de
              Dieu et le service aux autres. Nous croyons que chaque personne est précieuse aux
              yeux du Seigneur et mérite d'entendre la Bonne Nouvelle.
            </p>
            <p>
              Que vous soyez de passage ou que vous cherchiez un foyer spirituel, nos portes sont
              toujours ouvertes. Venez adorer, grandir et servir avec nous!
            </p>
            <Link to="/about" className="btn btn-primary" style={{ marginTop: '24px' }}>
              En savoir plus ›
            </Link>
          </div>
          <div className="welcome-stats">
            {[
              { num: '21+', label: 'Années de Ministère' },
              { num: '500+', label: 'Membres actifs' },
              { num: '6', label: 'Ministères' },
              { num: '1', label: 'Communauté unie' },
            ].map(({ num, label }) => (
              <div key={label} className="stat-card">
                <span className="stat-card__num">{num}</span>
                <span className="stat-card__label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Announcements ─────────────────────────────────────────────── */}
      {announcements.length > 0 && (
        <section className="section ann-section">
          <div className="container">
            <h2 className="section-title">Annonces</h2>
            <div className="gold-bar" />
            <div className="ann-list">
              {announcements.map((a) => (
                <div key={a.id} className="ann-card card">
                  <h3>{a.title}</h3>
                  <p>{a.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Upcoming Events ───────────────────────────────────────────── */}
      <section className="section events-preview">
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title">Événements à Venir</h2>
              <div className="gold-bar" />
            </div>
            <Link to="/events" className="btn btn-outline">Tous les événements ›</Link>
          </div>
          {events.length === 0 ? (
            <p className="text-muted">Aucun événement à venir pour le moment.</p>
          ) : (
            <div className="events-grid">
              {events.slice(0, 3).map((ev) => (
                <div key={ev.id} className="event-preview-card card">
                  <div className="event-date-badge">
                    <span className="event-date-badge__day">
                      {new Date(ev.date).getDate()}
                    </span>
                    <span className="event-date-badge__month">
                      {new Date(ev.date).toLocaleDateString('fr-FR', { month: 'short' })}
                    </span>
                  </div>
                  <div className="event-preview-info">
                    <h3>{ev.title}</h3>
                    {ev.location && <p className="event-location">📍 {ev.location}</p>}
                    <p className="event-desc">{ev.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Recent Sermons ────────────────────────────────────────────── */}
      <section className="section sermons-preview">
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title">Sermons Récents</h2>
              <div className="gold-bar" />
            </div>
            <Link to="/sermons" className="btn btn-outline">Tous les sermons ›</Link>
          </div>
          {sermons.length === 0 ? (
            <p className="text-muted">Aucun sermon disponible pour le moment.</p>
          ) : (
            <div className="sermons-list">
              {sermons.map((s) => (
                <div key={s.id} className="sermon-row card">
                  <div className="sermon-row__icon">🎙️</div>
                  <div className="sermon-row__info">
                    <h3>{s.title}</h3>
                    <p className="sermon-row__meta">
                      {s.speaker} · {formatDate(s.date)}
                      {s.scripture_reference && ` · ${s.scripture_reference}`}
                    </p>
                    {s.series && <span className="badge badge-gold">{s.series}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Gallery Teaser ────────────────────────────────────────────── */}
      <section className="section gallery-teaser">
        <div className="container">
          <div className="section-header">
            <div>
              <h2 className="section-title">La Vie de Notre Église</h2>
              <div className="gold-bar" />
            </div>
            <Link to="/gallery" className="btn btn-outline">Voir la galerie ›</Link>
          </div>
          <div className="gallery-grid">
            {galleryImages.map((img) => (
              <div key={img.id} className="gallery-thumb">
                <img src={img.image_url} alt={img.title} />
                <div className="gallery-thumb__overlay"><span>{img.title}</span></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ────────────────────────────────────────────────── */}
      <section className="cta-banner">
        <div className="container cta-banner__inner">
          <div>
            <h2>Avez-vous une demande de prière?</h2>
            <p>Nous prions pour chaque besoin. Partagez votre cœur avec nous.</p>
          </div>
          <Link to="/contact" className="btn btn-primary">Soumettre une prière ›</Link>
        </div>
      </section>
    </div>
  );
}
